import { useNavigation } from '@react-navigation/native';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import NudgeBanner from '@components/Nudge/NudgeBanner';
import {
  MemberTaskNudgeStep,
  useMemberTaskNudge,
} from '@hooks/useMemberTaskNudge';
import theme from '@themes/theme';

type Props = {
  // Pass for 4B/4C (TaskDetail) to scope banner to this task only
  currentTaskId?: string;
  // For 4C CTA — navigates to task chat
  projectUUId?: string;
  // Pass for 5A/5B (ChatDetail) to scope banner to the nudge chat
  currentChatId?: string;
  // 5A CTA callback — focuses the chat text input
  onFocusChatInput?: () => void;
  // 5B CTA callback — opens chat attachments modal with all handlers
  onOpenChatAttachments?: () => void;
  // Optional bottom override for chat context
  bottom?: number;
  // For 5A group chat gate: true if current user has already sent a message
  hasSentMessageInChat?: boolean;
  // For 5A group chat gate: true if at least one other member has sent a message
  hasOtherMemberSentMessage?: boolean;
  // For 5B group chat gate: true if current user has already shared a file
  hasSharedFileInChat?: boolean;
  // For 4C gate: true if the task has at least one other member besides the current user
  taskHasOtherMembers?: boolean;
};

const MemberTaskNudgeBanner: React.FC<Props> = ({
  currentTaskId,
  projectUUId,
  currentChatId,
  onFocusChatInput,
  onOpenChatAttachments,
  bottom: bottomOverride,
  hasSentMessageInChat,
  hasOtherMemberSentMessage,
  hasSharedFileInChat,
  taskHasOtherMembers,
}) => {
  const { t } = useTranslation('models');
  const navigation = useNavigation<any>();
  const {
    taskId,
    chatId,
    currentStep,
    isCurrentStepVisible,
    otherMemberSentInNudgeChat,
    dismissCurrentStep,
    completeStep,
  } = useMemberTaskNudge();
  const insets = useSafeAreaInsets();

  // Auto-skip 4C when task has no other members — prevents stuck flow
  const shouldAutoSkipSendMessage =
    currentStep === MemberTaskNudgeStep.SendMessage &&
    !!currentTaskId &&
    currentTaskId === taskId &&
    taskHasOtherMembers === false;

  useEffect(() => {
    if (shouldAutoSkipSendMessage) {
      completeStep(MemberTaskNudgeStep.SendMessage);
    }
  }, [shouldAutoSkipSendMessage, completeStep]);

  // Auto-complete 5A if the current user already sent a message in the nudge chat
  const shouldAutoCompleteFirstChat =
    currentStep === MemberTaskNudgeStep.SendFirstChat &&
    !!currentChatId &&
    !!chatId &&
    currentChatId === chatId &&
    !!hasSentMessageInChat;

  useEffect(() => {
    if (shouldAutoCompleteFirstChat) {
      completeStep(MemberTaskNudgeStep.SendFirstChat);
    }
  }, [shouldAutoCompleteFirstChat, completeStep]);

  // Auto-complete 5B if the current user already shared a file in the nudge chat
  const shouldAutoCompleteAddFile =
    currentStep === MemberTaskNudgeStep.AddFileToChat &&
    !!currentChatId &&
    !!chatId &&
    currentChatId === chatId &&
    !!hasSharedFileInChat;

  useEffect(() => {
    if (shouldAutoCompleteAddFile) {
      completeStep(MemberTaskNudgeStep.AddFileToChat);
    }
  }, [shouldAutoCompleteAddFile, completeStep]);

  if (!isCurrentStepVisible || !taskId) return null;

  const bottom =
    bottomOverride ??
    (Platform.OS === 'android' && insets.bottom > 0
      ? insets.bottom
      : theme.spacing.m);

  // 4A — shown on task list (no currentTaskId, no currentChatId)
  if (
    currentStep === MemberTaskNudgeStep.ExploreTask &&
    !currentTaskId &&
    !currentChatId
  ) {
    return (
      <NudgeBanner
        iconName='Hash'
        title={t('nudge.memberTask.exploreTask.title')}
        description={t('nudge.memberTask.exploreTask.description')}
        laterLabel={t('nudge.memberTask.exploreTask.later')}
        ctaLabel={t('nudge.memberTask.exploreTask.ctaLabel')}
        onLater={dismissCurrentStep}
        onCta={() =>
          navigation.navigate('tasks-stack', {
            screen: 'task-detail',
            params: { taskId },
          })
        }
      />
    );
  }

  // 5A/5B — shown in the nudge chat
  if (currentChatId && chatId && currentChatId === chatId) {
    if (currentStep === MemberTaskNudgeStep.SendFirstChat) {
      // Per design: only show when user has 0 messages sent AND ≥1 message from another member.
      // otherMemberSentInNudgeChat is a sticky flag that survives pagination — once true, stays true.
      const otherMemberHasSent =
        hasOtherMemberSentMessage || otherMemberSentInNudgeChat;
      if (hasSentMessageInChat || !otherMemberHasSent) return null;
      return (
        <NudgeBanner
          iconName='MessageSquare'
          title={t('nudge.memberTask.sendFirstChat.title')}
          description={t('nudge.memberTask.sendFirstChat.description')}
          laterLabel={t('nudge.memberTask.sendFirstChat.later')}
          ctaLabel={t('nudge.memberTask.sendFirstChat.ctaLabel')}
          onLater={dismissCurrentStep}
          onCta={() => onFocusChatInput?.()}
          bottom={bottom}
        />
      );
    }

    if (currentStep === MemberTaskNudgeStep.AddFileToChat) {
      // Per design: not shown if user already shared a file
      if (hasSharedFileInChat) return null;
      return (
        <NudgeBanner
          iconName='Image'
          title={t('nudge.memberTask.addFileToChat.title')}
          description={t('nudge.memberTask.addFileToChat.description')}
          laterLabel={t('nudge.memberTask.addFileToChat.later')}
          ctaLabel={t('nudge.memberTask.addFileToChat.ctaLabel')}
          onLater={dismissCurrentStep}
          onCta={() => onOpenChatAttachments?.()}
          bottom={bottom}
        />
      );
    }

    return null;
  }

  // 4B and 4C — shown on TaskDetail, scoped to nudge task
  if (!currentTaskId || currentTaskId !== taskId) return null;

  if (currentStep === MemberTaskNudgeStep.FirstUpdate) {
    return (
      <NudgeBanner
        iconName='Hash'
        title={t('nudge.memberTask.firstUpdate.title')}
        description={t('nudge.memberTask.firstUpdate.description')}
        laterLabel={t('nudge.memberTask.firstUpdate.later')}
        ctaLabel={t('nudge.memberTask.firstUpdate.ctaLabel')}
        onLater={dismissCurrentStep}
        onCta={() =>
          navigation.navigate('my-chat-menus-modal', {
            projectUUId,
            taskId: currentTaskId,
          })
        }
        bottom={bottom}
      />
    );
  }

  if (currentStep === MemberTaskNudgeStep.SendMessage) {
    // Per design: only show when the task has other members to message
    if (!taskHasOtherMembers) return null;
    return (
      <NudgeBanner
        iconName='MessageSquare'
        title={t('nudge.memberTask.sendMessage.title')}
        description={t('nudge.memberTask.sendMessage.description')}
        laterLabel={t('nudge.memberTask.sendMessage.later')}
        ctaLabel={t('nudge.memberTask.sendMessage.ctaLabel')}
        onLater={dismissCurrentStep}
        onCta={() =>
          navigation.navigate('task-members-select', {
            taskId: currentTaskId,
          })
        }
        bottom={bottom}
      />
    );
  }

  return null;
};

export default MemberTaskNudgeBanner;
